import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import bgImage from "../../components/UseCases/assets/UseCasesBgImage.svg";
import useSEO from "../../hooks/useSEO";

const PrivacyPolicy = () => {

  useSEO({
    title: "Privacy Policy - Beanbag AI",
    description: "Read Beanbag AI's Privacy Policy to understand how we collect, use, and protect your personal information. Learn about your data rights and our commitment to privacy.",
    canonical: "https://www.beanbag.ai/privacypolicy",
    keywords: "privacy policy, data protection, GDPR, CCPA, personal information, data rights, Beanbag AI",

    // Open Graph tags
    ogTitle: "Privacy Policy - Beanbag AI",
    ogDescription: "Understanding how Beanbag AI collects, uses, and protects your personal information. Your privacy rights and our data protection commitments.",
    ogImage: "https://www.beanbag.ai/images/privacy-social-card.jpg",
    ogUrl: "https://www.beanbag.ai/privacypolicy",
    ogType: "article",

    // Twitter Card tags
    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",

    // Additional meta tags
    robots: "index, follow",
    customMeta: {
      "article:published_time": "2025-03-20",
      "article:modified_time": "2025-03-20",
    },

    // JSON-LD structured data
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://www.beanbag.ai/privacypolicy#webpage",
      headline: "Privacy Policy",
      description: "Read Beanbag AI's Privacy Policy to understand how we collect, use, and protect your personal information.",
      url: "https://www.beanbag.ai/privacypolicy",
      datePublished: "2025-03-20",
      dateModified: "2025-03-20",
      publisher: {
        "@type": "Organization",
        name: "Beanbag AI",
        logo: {
          "@type": "ImageObject",
          url: "https://www.beanbag.ai/logo192.png",
        },
      },
      mainEntity: {
        "@type": "Article",
        headline: "Privacy Policy",
        datePublished: "2025-03-20",
        dateModified: "2025-03-20",
        author: {
          "@type": "Organization",
          name: "Beanbag AI"
        },
        publisher: {
          "@type": "Organization",
          name: "Beanbag AI",
          logo: {
            "@type": "ImageObject",
            url: "https://www.beanbag.ai/logo192.png",
          },
        }
      }
    },
  });
  // Define sections to match the blog post format
  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      content: `At Beanbag AI, accessible from www.beanbag.ai or app.beanbag.ai -one of our main priorities is the privacy of our visitors. This Privacy Policy document contains types of information that is collected and recorded by Beanbag AI and how we use it.

If you have additional questions or require more information about our Privacy Policy, do not hesitate to contact us.This Privacy Policy applies only to our online activities and is valid for visitors to our website with regards to the information that they shared and/or collect in Beanbag AI.

This policy is not applicable to any information collected offline or via channels other than this website.
`,
    },
    {
      id: "consent",
      title: "Consent",
      content:
        "By using our website, you hereby consent to our Privacy Policy and agree to its terms.",
    },
    {
      id: "information-collection",
      title: "Information We Collect",
      content: `The personal information that you are asked to provide, and the reasons why you are asked to provide it, will be made clear to you at the point we ask you to provide your personal information.

If you contact us directly, we may receive additional information about you such as your name, email address, phone number, the contents of the message and/or attachments you may send us, and any other information you may choose to provide.

When you register for an Account, we may ask for your contact information, including items such as name, company name, address, email address, and telephone number.
`,
    },
    {
      id: "information-usage",
      title: "How We Use Your Information",
      content: `We use the information we collect in various ways, including to:

- Provide, operate, and maintain our website
- Improve, personalize, and expand our website
- Understand and analyze how you use our website
- Develop new products, services, features, and functionality
- Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the website, and for marketing and promotional purposes
- Send you emails
- Find and prevent fraud`,
    },
    {
      id: "Google-usage",
      title: "How we use Google data on our platform",
      content: `- To store their email and name to send emails, read and compose on behalf of users to facilitate their Outbound campaigns.
- We affirm that Google Workspace APIs are not used to develop, improve, or train generalized/non-personalized AI and/or ML models.`,
    },
    {
      id: "protextion-usage",
      title: "Data Protection Mechanisms:",
      content: `- The user data collected on app.beanbag.ai is encrypted during transmission using HTTPS
- The user data collected is retained for 12 months for the Inactive users on the platform, after which the data is automatically deleted by sending timely intimation to the users.
- Beanbag AI conducts regular audits and security checks to protect User data.`,
    },
    {
      id: "logFiles-usage",
      title: "Log Files",
      content: `Beanbag AI follows a standard procedure of using log files. These files log visitors when they visit websites. All hosting companies do this and a part of hosting services' analytics. The information collected by log files include internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, referring/exit pages, and possibly the number of clicks.

These are not linked to any information that is personally identifiable. The purpose of the information is for analyzing trends, administering the site, tracking users' movement on the website, and gathering demographic information.
`,
    },
    {
      id: "Cookies-usage",
      title: "Cookies and Web Beacons",
      content: `Like any other website, Beanbag AI uses "cookies". These cookies are used to store information including visitors' preferences, and the pages on the website that the visitor accessed or visited. The information is used to optimize the users' experience by customizing our web page content based on visitors' browser type and/or other information`,
    },
    {
      id: "advertising-usage",
      title: "Advertising Partners Privacy Policies",
      content: `You may consult this list to find the Privacy Policy for each of the advertising partners of Beanbag AI.Third-party ad servers or ad networks uses technologies like cookies, JavaScript, or Web Beacons that are used in their respective advertisements and links that appear on Beanbag AI, which are sent directly to users' browser.

They automatically receive your IP address when this occurs. These technologies are used to measure the effectiveness of their advertising campaigns and/or to personalize the advertising content that you see on websites that you visit.

Note that Beanbag AI has no access to or control over these cookies that are used by third-party advertisers.`,
    },
    {
      id: "ThrdParty-usage",
      title: "Third Party Privacy Policies",
      content: `Beanbag AI's Privacy Policy does not apply to other advertisers or websites. Thus, we are advising you to consult the respective Privacy Policies of these third-party ad servers for more detailed information. It may include their practices and instructions about how to opt-out of certain options. 

You can choose to disable cookies through your individual browser options. To know more detailed information about cookie management with specific web browsers, it can be found at the browsers' respective websites.`,
    },
    {
      id: "CCPA-usage",
      title: "CCPA Privacy Rights (Do Not Sell My Personal Information)",
      content: `Under the CCPA, among other rights, California consumers have the right to:

Request that a business that collects a consumer's personal data disclose the categories and specific pieces of personal data that a business has collected about consumers.

Request that a business delete any personal data about the consumer that a business has collected.
Request that a business that sells a consumer's personal data, not sell the consumer's personal data.

If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us.`,
    },
    {
      id: "GDPR-usage",
      title: "GDPR Data Protection Rights",
      content: `We would like to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following:

The right to access – You have the right to request copies of your personal data. We may charge you a small fee for this service.
‍
The right to rectification – You have the right to request that we correct any information you believe is inaccurate. You also have the right to request that we complete the information you believe is incomplete.
‍
The right to erasure – You have the right to request that we erase your personal data, under certain conditions.

The right to restrict processing – You have the right to request that we restrict the processing of your personal data, under certain conditions.

The right to object to processing – You have the right to object to our processing of your personal data, under certain conditions.

The right to data portability – You have the right to request that we transfer the data that we have collected to another organisation, or directly to you, under certain conditions.

If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us.
`,
    },
    {
      id: "Child-usage",
      title: "Children's Information",
      content: `Another part of our priority is adding protection for children while using the internet. We encourage parents and guardians to observe, participate in, and/or monitor and guide their online activity.

Beanbag AI does not knowingly collect any Personal Identifiable Information from children under the age of 13. If you think that your child provided this kind of information on our website, we strongly encourage you to contact us immediately and we will do our best efforts to promptly remove such information from our records.
`,
    },
    {
      id: "Changes-usage",
      title: "Changes to This Privacy Policy",
      content: `We may update our Privacy Policy from time to time. Thus, we advise you to review this page periodically for any changes.
We will notify you of any changes by posting the new Privacy Policy on this page.

These changes are effective immediately, after they are posted on this page.

Our Privacy Policy was created with the help of the Privacy Policy Generator

Contact Us If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us on team@beanbag.ai`,
    },
  ];

  return (
    <Box
      sx={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ pt: 4, pb: 8, color: "white", marginTop: "100px" }}
      >
        <Box sx={{ mb: 6, textAlign: "center" }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            Privacy Policy
          </Typography>
        </Box>

        <Grid container>
          <Grid item xs={12}>
            {sections.map((section) => (
              <Box
                key={section.id}
                id={section.id}
                sx={{
                  mb: 4,
                  scrollMarginTop: "80px",
                }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    fontSize: {
                      xs: "1.75rem",
                      md: "1.2rem",
                    },
                  }}
                >
                  {section.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    whiteSpace: "pre-line",
                  }}
                >
                  {section.content}
                </Typography>
              </Box>
            ))}
            <Typography variant="body1" sx={{ mb: 3 }}>
              Last Updated: 20th March, 2025
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
